import NDSectorPanel from '../../nd/NDSectorPanel';
import { PMPFormField, PMPFormPanel } from '../../nd/utils';
import {
  antennaPanelFields,
  pmp450EquipmentFields,
  productPanel,
  power,
  antenna,
  ndAntennaGain,
  capacity,
} from './PMP450ViewConfig';
import { get, isNumber } from 'lodash';
import {
  band,
  getUserPower,
  isPMP450v,
  product,
  regulation,
} from '../../config-utils';
import NDEquipmentPanel from '../../nd/NDEquipmentPanel';
import { getSMEIRPConfig, getSMPowerConfig } from './config-utils';
import { syncFormState } from '../utils';

const radioQuantity: PMPFormField = {
  attrName: 'radio_qty',
  getter: 'radio_qty',
  refreshesChoices: true,
  label: 'Number of Component Carriers',
  afterOnChange(newValue, { getValues, setValue }, newChoices) {
    const radio0 = getValues('radios.0');
    const currentRadios = getValues('radios') || [];

    const antennas = Array.isArray(radio0.antennas)
      ? radio0.antennas
      : Object.values(radio0.antennas || {});

    if (newValue > 1) {
      // Create or update radios for other CCs
      for (let i = 1; i < newValue; i++) {
        const existingRadio = currentRadios[i];
        setValue(`radios.${i}`, {
          ...radio0,
          antennas: antennas,
          ...(existingRadio || {}),
        });
        setValue(`radios.${i}.antennas.0.sector`, i + 1);
        // Reset use_noise so that it reads from the AP
        setValue(`radios.${i}.power.use_noise`, false);
      }
    }

    // Trim excess radios if reducing the count
    if (currentRadios.length > newValue) {
      setValue('radios', currentRadios.slice(0, newValue));
    }
  },
};

const equipment: PMPFormPanel = {
  kind: 'equipment',
  title: 'Equipment',
  name: 'sm_equipment',
  component: NDEquipmentPanel,
  show({ formGetter }) {
    const radioQty = formGetter('radio_qty');
    return radioQty == null || radioQty === 1;
  },
  fields: [radioQuantity, ...pmp450EquipmentFields],
};

const multiRadioEquipment: PMPFormPanel = {
  kind: 'equipment',
  title: 'Equipment',
  name: 'sm_equipment',
  component: NDSectorPanel,
  // hacked radio quantity to appear on top
  // before titles, so ignore it fieldlayout
  // so 0 element indicates Component Carrier
  // with attrName radios.0.equipment.ap_radio_number
  fieldLayout: [[0], [1], [2]],
  show({ formGetter }) {
    return formGetter('radio_qty') > 1;
  },
  customClass: 'cnwave-power',
  sectorTitles: [],
  fields: [radioQuantity, product, capacity, regulation],
};

// Determines if power values should sync between CC1 and CC2
const shouldSyncSMPower = (formGetter) => {
  const product = formGetter('radios.0.equipment.product');
  const radio_qty = formGetter('radio_qty');
  if (isPMP450v(product) && radio_qty >= 2) {
    const band1 = formGetter('radios.0.equipment.band');
    const band2 = formGetter('radios.1.equipment.band');
    // Only sync when bands are actually the same
    return !band2 || band2 == band1;
  }
  return false;
};

// Determines when to show the info message
const getPMP450vSMPowerInfo = (formGetter, radioIdx) => {
  let message = null;

  const product = formGetter('radios.0.equipment.product');
  const radio_qty = formGetter('radio_qty');
  if (isPMP450v(product) && radio_qty >= 2) {
    const band1 = formGetter('radios.0.equipment.band');
    const band2 = formGetter('radios.1.equipment.band');
    // Show message when bands match OR when CC1 with 3 carriers (CC1/CC3 share power)
    if (!band2 || band2 == band1 || (radio_qty === 3 && radioIdx === 0)) {
      message = `
      Tx power is equally distributed between the CCs when
      the carriers are in the same band.
    `;
    }
  }

  return message;
};

export const sectorPower: PMPFormPanel = {
  kind: 'power',
  title: 'Power',
  name: 'sm_power',
  customClass: 'cnwave-power',
  sectorTitles({ formGetter }) {
    const radio_qty = formGetter('radio_qty');
    if (radio_qty === 3) {
      return [
        'Component Carrier 1/\nComponent Carrier 3',
        'Component Carrier 2',
      ];
    } else {
      return ['Component Carrier 1', 'Component Carrier 2'];
    }
  },
  show({ formGetter, ap }) {
    return (
      formGetter('radio_qty') > 1 &&
      get(ap, 'radios.0.equipment.carrier_qty') > 1
    );
  },
  fields: [
    getSMEIRPConfig(true),
    {
      ...getSMEIRPConfig(true, 1),
      warning({ sm }): string {
        return sm.radios[1]?.warnings?.eirp;
      },
    },
    getSMPowerConfig(true),
    getSMPowerConfig(true, 1),
    {
      ...getUserPower(true),
      nextValue(currentValue, newChoices, formGetter, attr) {
        return newChoices.power[0].user_power;
      },
      min: -30,
      max: 40,
      info: ({ formGetter }) => {
        return getPMP450vSMPowerInfo(formGetter, 0);
      },
      infoColor: 'yellow',
      afterOnChange(newValue, { setValue, getValues }, newChoices) {
        const shouldSync = shouldSyncSMPower(getValues);
        const userPower = getValues(`radios.0.power.user_power`);
        const radio_qty = getValues('radio_qty');
        if (isNumber(userPower)) {
          // Sync CC2 only when bands match
          if (shouldSync) {
            setValue('radios.1.power.use_user_power', true, {
              shouldValidate: true,
            });
            setValue('radios.1.power.user_power', userPower, {
              shouldValidate: true,
            });
          }
          // always sync with Component Carrier 3 (radios.2) if it exists
          if (radio_qty === 3) {
            setValue('radios.2.power.use_user_power', true, {
              shouldValidate: true,
            });
            setValue('radios.2.power.user_power', userPower, {
              shouldValidate: true,
            });
          }
        }
      },
      checkboxCompProps({ formGetter, formSetter }) {
        const shouldSync = shouldSyncSMPower(formGetter);
        const checked = formGetter(`radios.0.power.use_user_power`);
        const radio_qty = formGetter('radio_qty');
        // Sync CC2 only when bands match
        if (shouldSync) {
          formSetter('radios.1.power.use_user_power', checked);
        }
        // always sync with Component Carrier 3 (radios.2) if it exists
        if (radio_qty === 3) {
          formSetter('radios.2.power.use_user_power', checked);
        }
      },
    },
    {
      ...getUserPower(true, 1),
      nextValue(currentValue, newChoices, formGetter, attr) {
        return newChoices.power[1].user_power;
      },
      min: -30,
      max: 40,
      info: ({ formGetter }) => {
        return getPMP450vSMPowerInfo(formGetter, 1);
      },
      infoColor: 'yellow',
      editable: ({ formGetter }) => {
        const shouldSync = shouldSyncSMPower(formGetter);
        // Prevent editing when values need to sync (bands match)
        return !shouldSync;
      },
    },
  ],
};

export const pmp450vViewConfig = {
  syncFormState,
  equipment,
  multiRadioEquipment,
  product: productPanel,
  sectorPower,
  antenna: {
    ...antenna,
    fieldLayout: [[0], [1], [2], [3], [4], [5], [6], [7], [8], [9, 10]],
    fields: [
      ...antennaPanelFields,
      {
        ...ndAntennaGain,
        label: 'ND Antenna Gain - Carrier 2',
        getter: 'radios.1.antennas.0.ap_antenna_gain',
        show({ formGetter, ap }) {
          return (
            formGetter('radio_qty') > 1 &&
            get(ap, 'radios.0.equipment.carrier_qty') > 1
          );
        },
        warning({ formGetter }) {
          return formGetter(`radios.1.warnings.gain_warning`);
        },
      },
    ],
  },
  power: {
    ...power,
    show({ formGetter, ap }) {
      const radioQty = formGetter('radio_qty');
      const carrierQty = get(ap, 'radios.0.equipment.carrier_qty');
      // if the ND is 450 family product other than 450v and SM is 45v product
      // then radioQty is null before save and carrierQty is null after save
      // so we need to check both cases (radioQty == null || carrierQty == null)
      return (
        radioQty == null ||
        carrierQty == null ||
        (carrierQty >= 1 && radioQty == 1) ||
        (carrierQty == 1 && radioQty >= 1)
      );
    },
  },
};
